import {
  customElement,
  DeesElement,
  property,
  html,
  cssManager,
  css,
  query,
  type TemplateResult,
} from '@design.estate/dees-element';

import { demoFunc } from './dees-searchbar.demo.js';
import { themeDefaultStyles } from '../../00theme.js';

declare global {
  interface HTMLElementTagNameMap {
    'dees-searchbar': DeesSearchbar;
  }
}

@customElement('dees-searchbar')
export class DeesSearchbar extends DeesElement {
  // DEMO
  public static demo = demoFunc;
  public static demoGroups = ['Utility'];

  // STATIC
  public static styles = [
    themeDefaultStyles,
    cssManager.defaultStyles,
    css`
      :host {
        padding: var(--dees-spacing-xl);
        font-family: var(--dees-font-family);
        display: block;
        background: var(--dees-color-bg-canvas);
      }

      .searchboxContainer {
        position: relative;
        margin: auto;
        max-width: 800px;
        background: var(--dees-color-fill-tertiary);
        --boxHeight: var(--dees-control-height-xl);
        height: var(--boxHeight);
        border-radius: var(--boxHeight);
        display: grid;
        grid-template-columns: 1fr max-content;
        justify-content: center;
        align-items: center;
        border: 1px solid var(--dees-color-border-subtle);
        transition: border-color var(--dees-transition-fast) var(--dees-ease-standard), box-shadow var(--dees-transition-fast) var(--dees-ease-standard);
      }

      .searchboxContainer:focus-within {
        border-color: var(--dees-color-accent-primary);
        box-shadow: 0 0 0 3px var(--dees-color-focus-ring);
      }

      input {
        height: 100%;
        width: 100%;
        border: none;
        background: none;
        color: var(--dees-color-text-primary);
        padding: 0 var(--dees-spacing-lg);
        outline: none;
        font-family: inherit;
        font-size: var(--dees-font-control-size);
      }

      input::placeholder {
        color: var(--dees-color-label-quaternary);
      }

      .searchButton {
        --buttonPadding: var(--dees-spacing-xs);
        background: var(--dees-color-accent-primary);
        color: var(--dees-color-on-accent);
        line-height: calc(var(--boxHeight) - var(--buttonPadding) - var(--buttonPadding));
        border-radius: var(--boxHeight);
        transform: scale(1);
        transform-origin: 50% 50%;
        text-align: center;
        min-width: 112px;
        padding: 0 var(--dees-spacing-lg);
        font-size: var(--dees-font-control-size);
        font-weight: var(--dees-font-control-weight);
        transition: transform var(--dees-transition-fast) var(--dees-ease-standard), background var(--dees-transition-fast) var(--dees-ease-standard);
        margin-right: var(--buttonPadding);
        user-select: none;
        cursor: pointer;
      }

      .searchButton:hover {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 88%, var(--dees-color-text-primary));
      }

      .searchButton:active {
        background: color-mix(in srgb, var(--dees-color-accent-primary) 78%, var(--dees-color-text-primary));
        transform: scale(0.98);
      }

      .filters {
        margin: auto;
        max-width: 800px;
      }

      @media (prefers-reduced-motion: reduce) {
        .searchboxContainer,
        .searchButton {
          transition: none;
        }

        .searchButton:active {
          transform: none;
        }
      }
    `,
  ];

  // INSTANCE

  @property()
  accessor filters = [];


  @query('input')
  accessor searchInput!: HTMLInputElement;

  @query('.searchButton')
  accessor searchButton!: HTMLElement;

  constructor() {
    super();
  }

  public render(): TemplateResult {
    return html`
      <div class="searchboxContainer">
        <input type="text" placeholder="Your Skills (e.g. TypeScript, Rust, Projectmanagement)" />
        <div class="searchButton">Search -></div>
      </div>
      ${this.filters.length > 0 ? html`
        <div class="filters">
        <dees-heading level="hr-small">Filters</dees-heading>
        <dees-input-dropdown .label=${'location'}></dees-input-dropdown>
      </div>  
      ` : html``}
    `;
  }
  /**
   * Lifecycle: after first render, wire up events for input and submit actions
   */
  public firstUpdated(): void {
    // dispatch change on each input
    this.searchInput.addEventListener('input', () => {
      this.dispatchEvent(new CustomEvent('search-changed', {
        bubbles: true,
        composed: true,
        detail: { value: this.searchInput.value }
      }));
    });
    // submit on Enter key
    this.searchInput.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter') {
        this._dispatchSubmit();
      }
    });
    // submit on button click
    this.searchButton.addEventListener('click', () => this._dispatchSubmit());
  }

  /**
   * Dispatch a submit event with the current search value
   */
  private _dispatchSubmit(): void {
    this.dispatchEvent(new CustomEvent('search-submit', {
      bubbles: true,
      composed: true,
      detail: { value: this.searchInput.value }
    }));
  }
}
